//This file is a route configuration file and is expected to have a lot of lines
import { WbText } from '@workbench/react';
import React from 'react';
import { Outlet } from 'react-router-dom';
import { AdminPages, Params } from '../utils/RouteEnums';
import AdminMissingPermission from '../components/admin/AdminMissingPermission';

// Lazy-loaded components
const AdminRoute = React.lazy(() => import('./AdminRoute'));
const BeamRegistrationDetailsWrapper = React.lazy(() => import('../components/admin/client/BeamRegistrationWrapper'));
const ChangeManageRegistrationWrapper = React.lazy(
    () => import('../components/admin/client/ChangeRegistrationWrapper')
);
const RegistrationDetailsWrapper = React.lazy(() => import('../components/admin/client/ClientsWrapper'));
const DashboardWrapper = React.lazy(() => import('../components/admin/client/DashboardWrapper'));
const BeamInvoicesWrapper = React.lazy(() => import('../components/admin/client/invoices/BeamInvoicesWrapper'));
const BeamInvoicesDetailsWrapper = React.lazy(
    () => import('../components/admin/client/invoices/BeamInvoicesDetailsWrapper')
);
const ProductsWrapper = React.lazy(() => import('../components/admin/products/ProductsWrapper'));
const AdminProductDetails = React.lazy(() => import('../components/admin/products/AdminProductDetails'));
const AdminFormulationDetailsWrapper = React.lazy(
    () => import('../components/admin/products/AdminFormulationDetailsWrapper')
);
const ProcessesOverviewWrapper = React.lazy(() => import('../components/admin/products/ProcessesOverviewWrapper'));
const ProcessDetail = React.lazy(() => import('../components/admin/products/processOverviewDetails/ProcessDetail'));
const OilTestingEngineMasterDataWrapper = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/OilTestingEngineMasterDataWrapper')
);
const EnginTypeEdit = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/engineType/EnginTypeEdit')
);
const EnginTypeNew = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/engineType/EnginTypeNew')
);
const InvoiceDataEdit = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/invoiceData/InvoiceDataEdit')
);
const InvoiceDataNew = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/invoiceData/InvoiceDataNew')
);
const TestBenchResultEdit = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/testBenchResults/TestBenchResultEdit')
);
const TestBenchResultNew = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/testBenchResults/TestBenchResultNew')
);
const TestStandEdit = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/testStand/TestStandEdit')
);
const TestStandNew = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/testStand/TestStandNew')
);
const TestSubstancesEdit = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/testSubstances/TestSubstancesEdit')
);
const TestSubstancesNew = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineMasterData/testSubstances/TestSubstancesNew')
);
const OilTestingEngineTestsDetailsWrapper = React.lazy(
    () =>
        import(
            '../components/admin/testAggregates/oilTestingEngineTests/oilTestingEngineTests/OilTestingEngineTestsDetailsWrapper'
        )
);
const OilTestingEngineTestsWrapper = React.lazy(
    () => import('../components/admin/testAggregates/oilTestingEngineTests/OilTestingEngineTestsWrapper')
);
const BStoffApiWrapper = React.lazy(() => import('../components/admin/externalApplications/BStoffApiWrapper'));
const FluidFinderWrapper = React.lazy(
    () => import('../components/admin/externalApplications/fluidfinder/FluidFinderWrapper')
);
const ManagementNewsCreate = React.lazy(() => import('../components/admin/management/news/ManagementNewsCreate'));
const ManagementNewsEdit = React.lazy(() => import('../components/admin/management/news/ManagementNewsEdit'));
const ManagementNewsWrapper = React.lazy(() => import('../components/admin/management/news/ManagementNewsWrapper'));
const ManagementServiceAreaWrapper = React.lazy(
    () => import('../components/admin/management/serviceArea/ManagementServiceAreaWrapper')
);
const ManagementDownloadsEdit = React.lazy(
    () => import('../components/admin/management/serviceArea/downloadsService/ManagementDownloadsEdit')
);
const ManagementDownloadsNew = React.lazy(
    () => import('../components/admin/management/serviceArea/downloadsService/ManagementDownloadsNew')
);
const AdditiveBaseOilCareWrapper = React.lazy(
    () => import('../components/admin/masterData/additiveBaseOilCare/AdditiveBaseOilCareWrapper')
);
const MasterDataAdditivesEdit = React.lazy(
    () => import('../components/admin/masterData/additiveBaseOilCare/additive/MasterDataAdditivesEdit')
);
const MasterDataAdditivesNew = React.lazy(
    () => import('../components/admin/masterData/additiveBaseOilCare/additive/MasterDataAdditivesNew')
);
const MasterDataBaseOilsEdit = React.lazy(
    () => import('../components/admin/masterData/additiveBaseOilCare/baseoils/MasterDataBaseOilsEdit')
);
const MasterDataBaseOilsNew = React.lazy(
    () => import('../components/admin/masterData/additiveBaseOilCare/baseoils/MasterDataBaseOilsNew')
);
const AreaCareWrapper = React.lazy(() => import('../components/admin/masterData/areasCare/AreaCareWrapper'));
const MasterDataContainerSizeDetails = React.lazy(
    () => import('../components/admin/masterData/areasCare/containerSize/MasterDataContainerSizeDetails')
);
const MasterDataCostEdit = React.lazy(() => import('../components/admin/masterData/areasCare/cost/MasterDataCostEdit'));
const MasterDataCostNew = React.lazy(() => import('../components/admin/masterData/areasCare/cost/MasterDataCostNew'));
const MasterDataSalesAreasEdit = React.lazy(
    () => import('../components/admin/masterData/areasCare/salesAreas/MasterDataSalesAreasEdit')
);
const MasterDataSalesAreasNew = React.lazy(
    () => import('../components/admin/masterData/areasCare/salesAreas/MasterDataSalesAreasNew')
);
const ProductCareWrapper = React.lazy(() => import('../components/admin/masterData/productCare/ProductCareWrapper'));
const MasterDataQualityLinesEdit = React.lazy(
    () => import('../components/admin/masterData/productCare/qualityLine/MasterDataQualityLinesEdit')
);
const MasterDataQualityLinesNew = React.lazy(
    () => import('../components/admin/masterData/productCare/qualityLine/MasterDataQualityLinesNew')
);
const MasterDataSaeClassEdit = React.lazy(
    () => import('../components/admin/masterData/productCare/saeClass/MasterDataSaeClassEdit')
);
const MasterDataSaeClassNew = React.lazy(
    () => import('../components/admin/masterData/productCare/saeClass/MasterDataSaeClassNew')
);
const SheetCareWrapper = React.lazy(() => import('../components/admin/masterData/sheetCare/SheetCareWrapper'));
const MasterDataOperatingFluidTypesEdit = React.lazy(
    () => import('../components/admin/masterData/sheetCare/operatingFluidTypes/MasterDataOperatingFluidTypesEdit')
);
const MasterDataOperatingFluidTypesNew = React.lazy(
    () => import('../components/admin/masterData/sheetCare/operatingFluidTypes/MasterDataOperatingFluidTypesNew')
);
const MasterDataSheetsEdit = React.lazy(
    () => import('../components/admin/masterData/sheetCare/sheets/MasterDataSheetsEdit')
);
const MasterDataSheetsNew = React.lazy(
    () => import('../components/admin/masterData/sheetCare/sheets/MasterDataSheetsNew')
);
const MasterDataCompatibleSheets = React.lazy(
    () => import('../components/admin/masterData/sheetCare/compatibleSheets/MasterDataCompatibleSheets')
);
const TestCareWrapper = React.lazy(() => import('../components/admin/masterData/testsCare/TestCareWrapper'));
const BeamRegistrationChangeWrapper = React.lazy(
    () => import('../components/admin/client/changeClientData/BeamRegistrationChangeWrapper')
);
const BeamRegistrationManageWrapper = React.lazy(
    () => import('../components/admin/client/manageCompanyRegistration/wrapper/BeamRegistrationManageWrapper')
);
const ManageCompanyRegistrationOverviewWrapper = React.lazy(
    () =>
        import('../components/admin/client/manageCompanyRegistration/wrapper/ManageCompanyRegistrationOverviewWrapper')
);
const MangmentFAQNew = React.lazy(() => import('../components/admin/management/serviceArea/faq/MangmentFAQNewOrEdit'));

const MasterDataSubstanceType = React.lazy(
    () => import('../components/admin/masterData/additiveBaseOilCare/substanceTypes/MasterDataSubstanceType')
);

export const adminRoutes = [
    {
        getComponent: () => <AdminRoute />,
        path: AdminPages.ADMIN,
        routes: [
            {
                getComponent: () => <Outlet />,
                path: AdminPages.CLIENT,
                routes: [
                    {
                        getComponent: () => <BeamRegistrationDetailsWrapper />,
                        path: AdminPages.CLIENT_REG_DETAILS,
                        routes: [
                            {
                                getComponent: () => <RegistrationDetailsWrapper />,
                                path: `:${Params.REGISTRATION_ID}`,
                            },
                        ],
                    },
                    {
                        getComponent: () => <BeamRegistrationManageWrapper />,
                        path: AdminPages.CLIENT_REG_MANAGE,
                        routes: [
                            {
                                getComponent: () => <ManageCompanyRegistrationOverviewWrapper />,
                                path: `:${Params.REGISTRATION_ID}`,
                            },
                        ],
                    },
                    {
                        getComponent: () => <BeamRegistrationChangeWrapper />,
                        path: AdminPages.CLIENT_REG_CHANGE,
                        routes: [
                            {
                                getComponent: () => <ChangeManageRegistrationWrapper />,
                                path: `:${Params.REGISTRATION_ID}`,
                            },
                        ],
                    },
                    {
                        getComponent: () => <BeamInvoicesWrapper />,
                        path: AdminPages.CLIENT_INVOICES,
                        routes: [
                            {
                                getComponent: () => <BeamInvoicesDetailsWrapper />,
                                path: `:${Params.ID}`,
                            },
                        ],
                    },
                ],
            },
            {
                getComponent: () => <DashboardWrapper />,
                path: AdminPages.DASHBOARD,
            },
            {
                getComponent: () => <Outlet />,
                path: AdminPages.PRODUCTS,
                routes: [
                    {
                        getComponent: () => <ProductsWrapper />,
                        path: AdminPages.PRODUCTS_OVERVIEW,
                        routes: [
                            {
                                getComponent: () => <AdminProductDetails />,
                                path: `:${Params.PRODUCT_ID}`,
                            },
                        ],
                    },
                    {
                        getComponent: () => <ProductsWrapper />,
                        path: AdminPages.PRODUCTS_OVERVIEW,
                        routes: [
                            {
                                getComponent: () => <Outlet />,
                                path: `:${Params.PRODUCT_ID}`,
                                routes: [
                                    {
                                        getComponent: () => <AdminFormulationDetailsWrapper />,
                                        path: `:${Params.FORMULATION_ID}`,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        getComponent: () => <ProcessesOverviewWrapper />,
                        path: AdminPages.PROCESSES_OVERVIEW,
                        routes: [
                            {
                                getComponent: () => <ProcessDetail />,
                                path: `:${Params.PRODUCT_APPROVAL_ID}`,
                            },
                        ],
                    },
                ],
            },
            {
                getComponent: () => <Outlet />,
                path: AdminPages.MASTER_DATA,
                routes: [
                    {
                        getComponent: () => <SheetCareWrapper />,
                        path: AdminPages.MASTER_DATA_SHEET_CARE,
                        routes: [
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_SHEETS,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataSheetsEdit />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataSheetsNew />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                getComponent: () => <MasterDataCompatibleSheets />,
                                path: AdminPages.MASTER_DATA_COMPATIBLE_SHEETS,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_OPERATING_FLUID_TYPES,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataOperatingFluidTypesEdit />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataOperatingFluidTypesNew />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        getComponent: () => <ProductCareWrapper />,
                        path: AdminPages.MASTER_DATA_PRODUCT_CARE,
                        routes: [
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_QUALITY_LINES,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataQualityLinesEdit />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataQualityLinesNew />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_SAE_CLASSES,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataSaeClassEdit />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataSaeClassNew />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        getComponent: () => <AreaCareWrapper />,
                        path: AdminPages.MASTER_DATA_AREA_CARE,
                        routes: [
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_COSTS,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataCostEdit />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataCostNew />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_SALES_AREAS,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataSalesAreasEdit />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataSalesAreasNew />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_CONTAINER_SIZE,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataContainerSizeDetails />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataContainerSizeDetails />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        getComponent: () => <TestCareWrapper />,
                        path: AdminPages.MASTER_DATA_TEST_CARE,
                        routes: [
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_MANAGE_TESTS,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_MANAGE_TEST_PARAMETERS,
                            },
                        ],
                    },
                    {
                        getComponent: () => <AdditiveBaseOilCareWrapper />,
                        path: AdminPages.MASTER_DATA_ADDITIVE_BASEOIL_CARE,
                        routes: [
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_ADDITIVE,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataAdditivesEdit />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataAdditivesNew />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_BASE_OILS,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataBaseOilsEdit />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataBaseOilsNew />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MASTER_DATA_SUBSTANCE_TYPE,
                                routes: [
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_EDIT,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataSubstanceType />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                    {
                                        getComponent: () => <Outlet />,
                                        path: AdminPages.MASTER_DATA_NEW,
                                        routes: [
                                            {
                                                getComponent: () => <MasterDataSubstanceType />,
                                                path: `:${Params.ID}`,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                getComponent: () => <Outlet />,
                path: AdminPages.TEST_AGGREGATES,
                routes: [
                    {
                        getComponent: () => <OilTestingEngineMasterDataWrapper />,
                        path: AdminPages.TEST_AGGREGATES_OIL_TESTING_ENGINE_MASTER_DATA,
                        routes: [
                            {
                                getComponent: () => <EnginTypeNew />,
                                path: AdminPages.ENGINE_TYPES_NEW,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.ENGINE_TYPES_EDIT,
                                routes: [
                                    {
                                        getComponent: () => <EnginTypeEdit />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                            {
                                getComponent: () => <TestStandNew />,
                                path: AdminPages.TEST_STAND_NEW,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.TEST_STAND_EDIT,
                                routes: [
                                    {
                                        getComponent: () => <TestStandEdit />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                            {
                                getComponent: () => <TestSubstancesNew />,
                                path: AdminPages.TEST_SUBSTANCES_NEW,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.TEST_SUBSTANCES_EDIT,
                                routes: [
                                    {
                                        getComponent: () => <TestSubstancesEdit />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                            {
                                getComponent: () => <TestBenchResultNew />,
                                path: AdminPages.TEST_BENCH_RESULTS_NEW,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.TEST_BENCH_RESULTS_EDIT,
                                routes: [
                                    {
                                        getComponent: () => <TestBenchResultEdit />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                            {
                                getComponent: () => <InvoiceDataNew />,
                                path: AdminPages.INVOICE_DATA_NEW,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.INVOICE_DATA_EDIT,
                                routes: [
                                    {
                                        getComponent: () => <InvoiceDataEdit />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        getComponent: () => <OilTestingEngineTestsWrapper />,
                        path: AdminPages.TEST_AGGREGATES_OIL_TESTING_ENGINE_TESTS,
                        routes: [
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.TEST_AGGREGATES_OIL_TESTING_ENGINE_TESTS_TEST_DETAILS,
                                routes: [
                                    {
                                        getComponent: () => <OilTestingEngineTestsDetailsWrapper />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                            {
                                getComponent: () => <OilTestingEngineTestsDetailsWrapper />,
                                path: AdminPages.TEST_AGGREGATES_OIL_TESTING_ENGINE_TESTS_NEW,
                            },
                        ],
                    },
                    {
                        getComponent: () => <WbText>Under construction</WbText>,
                        path: '*',
                    },
                ],
            },

            {
                getComponent: () => <Outlet />,
                path: AdminPages.EXTERNAL_APPLICATIONS,
                routes: [
                    {
                        getComponent: () => <BStoffApiWrapper />,
                        path: AdminPages.EXTERNAL_APPLICATIONS_BMATERIAL_API,
                    },
                    {
                        getComponent: () => <FluidFinderWrapper />,
                        path: AdminPages.EXTERNAL_APPLICATIONS_FLUIDFINDER,
                    },
                    {
                        getComponent: () => <WbText>Under construction</WbText>,
                        path: '*',
                    },
                ],
            },
            {
                getComponent: () => <Outlet />,
                path: AdminPages.MANAGEMENT,
                routes: [
                    {
                        getComponent: () => <ManagementNewsWrapper />,
                        path: AdminPages.MANAGEMENT_NEWS,
                        routes: [
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MANAGEMENT_NEWS_CREATE,
                                routes: [
                                    {
                                        getComponent: () => <ManagementNewsCreate />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MANAGEMENT_NEWS_EDIT,
                                routes: [
                                    {
                                        getComponent: () => <ManagementNewsEdit />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        getComponent: () => <ManagementServiceAreaWrapper />,
                        path: AdminPages.MANAGEMENT_SERVICE_AREA,
                        routes: [
                            {
                                getComponent: () => <ManagementDownloadsNew />,
                                path: AdminPages.MANAGEMENT_SERVICE_AREA_NEW,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MANAGEMENT_SERVICE_AREA_EDIT,
                                routes: [
                                    {
                                        getComponent: () => <ManagementDownloadsEdit />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                            {
                                getComponent: () => <MangmentFAQNew />,
                                path: AdminPages.MANAGEMENT_FAQ_NEW,
                            },
                            {
                                getComponent: () => <Outlet />,
                                path: AdminPages.MANAGEMENT_FAQ_EDIT,
                                routes: [
                                    {
                                        getComponent: () => <MangmentFAQNew />,
                                        path: `:${Params.ID}`,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        getComponent: () => <AdminMissingPermission />,
        path: 'admin/missing-permission',
    },
];
